import React, { useEffect, useRef, useState } from "react";
import { Check, Mic, X } from "lucide-react";
import VoicePanelShell from "./VoicePanelShell";

interface VoiceTranscriptConfirmProps {
  /** What whisper heard. Editable — the confirmed text is what gets interpreted. */
  transcript: string;
  /** Confirms the text on screen (check button / Enter). */
  onConfirm: (text: string) => void;
  /** Abandons the attempt and puts the search form back. */
  onCancel: () => void;
}

/**
 * Step 3: whisper's transcript, shown before anything is sent to the server.
 * The text is a real input, so a misheard airport can be fixed here rather
 * than bouncing through a failed search — the check hands the text on to
 * /Voice/Interpret, the × next to it drops the attempt.
 */
const VoiceTranscriptConfirm: React.FC<VoiceTranscriptConfirmProps> = ({
  transcript,
  onConfirm,
  onCancel,
}) => {
  const [text, setText] = useState(transcript);
  const inputRef = useRef<HTMLInputElement>(null);

  // A fresh transcript replaces whatever the previous attempt left behind.
  useEffect(() => {
    setText(transcript);
  }, [transcript]);

  useEffect(() => {
    // Caret at the end, not over the text: selecting all would make the first
    // keystroke wipe a transcript that is usually right.
    const input = inputRef.current;
    if (!input) return;
    input.focus();
    const end = input.value.length;
    input.setSelectionRange(end, end);
  }, []);

  const canConfirm = text.trim().length > 0;

  return (
    <VoicePanelShell>
      <div className="flex flex-col items-center justify-center">
        {/* Mic badge sitting on a dashed trail — the recording is over, so it
            is a marker here, not a control. */}
        <div className="relative flex w-full max-w-[560px] items-center justify-center">
          <span
            aria-hidden="true"
            className="absolute left-0 right-0 top-1/2 h-px -translate-y-1/2"
            style={{
              backgroundImage:
                "repeating-linear-gradient(to right, rgba(255,188,0,0.55) 0 8px, transparent 8px 16px)",
              maskImage: "linear-gradient(to right, transparent, black 22%, black 78%, transparent)",
              WebkitMaskImage:
                "linear-gradient(to right, transparent, black 22%, black 78%, transparent)",
            }}
          />
          <span className="voice-confirm-mic relative z-10 flex h-11 w-11 items-center justify-center rounded-full bg-[#0A0A0A] ring-2 ring-white">
            <Mic className="h-[18px] w-[18px] text-white" />
          </span>
        </div>

        <div className="mt-5 flex w-full max-w-[440px] items-center gap-2.5 rounded-xl border border-white/10 bg-[#151515] px-3 py-2.5 shadow-[0_6px_24px_rgba(0,0,0,0.45)] focus-within:border-white/25 focus-within:ring-1 focus-within:ring-white/25">
          <button
            type="button"
            onClick={onCancel}
            aria-label="Discard what was heard"
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-white/55 transition-colors hover:bg-white/10 hover:text-white cursor-pointer"
          >
            <X className="h-4 w-4" />
          </button>

          <input
            ref={inputRef}
            type="text"
            value={text}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && canConfirm) onConfirm(text);
              if (event.key === "Escape") onCancel();
            }}
            aria-label="Edit what was heard"
            className="min-w-0 flex-1 bg-transparent font-inter text-[15px] text-white caret-[#FFBC00] outline-none focus:outline-none focus:ring-0 placeholder:text-white/30"
            placeholder="Say where you want to fly"
          />

          <button
            type="button"
            onClick={() => onConfirm(text)}
            disabled={!canConfirm}
            aria-label="Use this text"
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#FFBC00] text-[#192227] transition-colors hover:bg-[#F0AF00] disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
          >
            <Check className="h-3.5 w-3.5 stroke-[3.5]" />
          </button>
        </div>
      </div>

      <style>{`
        .voice-confirm-mic {
          box-shadow: 0 0 0 6px rgba(0,0,0,0.9), 0 0 26px 6px rgba(253,193,30,0.28);
        }
      `}</style>
    </VoicePanelShell>
  );
};

export default VoiceTranscriptConfirm;
